import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { getActivityCalendar, getTodayString } from '../../utils/streakManager';
import { playSound } from '../../utils/soundManager';

const ActivityCalendar = ({ activityDates = [], onClose }) => {
  const [calendarData, setCalendarData] = useState([]);
  const [currentMonthIndex, setCurrentMonthIndex] = useState(0);

  useEffect(() => {
    const calendar = getActivityCalendar(activityDates, 12); // Show 12 months
    setCalendarData(calendar);
    setCurrentMonthIndex(calendar.length - 1); // Start with current month
  }, [activityDates]);

  const handlePrevMonth = () => {
    if (currentMonthIndex > 0) {
      setCurrentMonthIndex(currentMonthIndex - 1);
      playSound('click');
    }
  };

  const handleNextMonth = () => {
    if (currentMonthIndex < calendarData.length - 1) {
      setCurrentMonthIndex(currentMonthIndex + 1);
      playSound('click');
    }
  };

  const handleClose = () => {
    playSound('click');
    onClose();
  };

  if (calendarData.length === 0) return null;

  const currentMonth = calendarData[currentMonthIndex];
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Calculate statistics
  const totalActiveDays = activityDates.length;
  const thisMonthActive = currentMonth.days.filter(d => d.active).length;
  const activeDaysThisMonth = currentMonth.days.filter(d => d.date && !d.isFuture).length;
  const completionRate = activeDaysThisMonth > 0 
    ? Math.round((thisMonthActive / activeDaysThisMonth) * 100) 
    : 0;

  return (
    <div className="activity-calendar-overlay" onClick={handleClose}>
      <div 
        className="activity-calendar-modal animate-scaleIn" 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="calendar-header">
          <h2>Activity Calendar</h2>
          <button className="calendar-close hover-scale" onClick={handleClose}>
            <X size={24} />
          </button>
        </div>

        <div className="calendar-stats">
          <div className="calendar-stat">
            <span className="stat-value">{totalActiveDays}</span>
            <span className="stat-label">Total Days</span>
          </div>
          <div className="calendar-stat">
            <span className="stat-value">{thisMonthActive}</span>
            <span className="stat-label">This Month</span>
          </div>
          <div className="calendar-stat">
            <span className="stat-value">{completionRate}%</span>
            <span className="stat-label">Completion</span>
          </div>
        </div>

        <div className="calendar-navigation">
          <button 
            className="nav-btn hover-scale"
            onClick={handlePrevMonth}
            disabled={currentMonthIndex === 0}
          >
            <ChevronLeft size={24} />
          </button>
          
          <h3>{currentMonth.monthName} {currentMonth.year}</h3>
          
          <button 
            className="nav-btn hover-scale"
            onClick={handleNextMonth}
            disabled={currentMonthIndex === calendarData.length - 1}
          >
            <ChevronRight size={24} />
          </button>
        </div>

        <div className="calendar-grid">
          {/* Week day headers */}
          <div className="calendar-weekdays">
            {weekDays.map(day => (
              <div key={day} className="weekday-header">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar days */}
          <div className="calendar-days">
            {currentMonth.days.map((day, index) => (
              <div
                key={index}
                className={`calendar-day ${
                  day.date ? 'has-date' : 'empty'
                } ${
                  day.active ? 'active' : ''
                } ${
                  day.isToday ? 'today' : ''
                } ${
                  day.isFuture ? 'future' : ''
                }`}
                title={day.active ? `Active on ${day.dateString}` : ''}
              >
                {day.date && (
                  <>
                    <span className="day-number">{day.date}</span>
                    {day.active && <div className="activity-dot"></div>}
                    {day.isToday && <div className="today-ring"></div>}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="calendar-legend">
          <div className="legend-item">
            <div className="legend-box active"></div>
            <span>Active Day</span>
          </div>
          <div className="legend-item">
            <div className="legend-box today"></div>
            <span>Today</span>
          </div>
          <div className="legend-item">
            <div className="legend-box inactive"></div>
            <span>Inactive Day</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityCalendar;
